import * as THREE from "three";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { nanoid } from "nanoid";
import {
  applyProjectionsToMaterial,
  createProjectionBackdrop,
  repositionProjectionBackdrop,
} from "./projection";
import { CAMERA_NEAR, CAMERA_FAR } from "./cameraConstants";

const DEG2RAD = Math.PI / 180;

const GEOMETRY_FACTORIES = {
  plane: () => new THREE.PlaneGeometry(1, 1),
  cube: () => new THREE.BoxGeometry(1, 1, 1),
  sphere: () => new THREE.SphereGeometry(0.5, 32, 16),
  cone: () => new THREE.ConeGeometry(0.5, 1, 32),
  cylinder: () => new THREE.CylinderGeometry(0.5, 0.5, 1, 32),
};

export const GEOMETRY_TYPES = Object.keys(GEOMETRY_FACTORIES);

// Shared by createNode's mesh branch and buildNodesFromGLTFScene's imported
// mesh leaves — a "custom" node's color/roughness/metalness/unlit are unused
// (its material comes from the GLB — see buildObject3D) but still need some
// value to satisfy the schema/PropertiesPanel the same as any other mesh.
const MESH_DEFAULTS = {
  color: "#8899aa",
  roughness: 1,
  metalness: 0,
  unlit: false,
  ignoreProjections: false,
};

// The layer editor-only gizmos/helpers live on (AxesHelper for "null" nodes,
// specifically — see buildObject3D). Every camera that renders the *live*
// viewport enables this layer so gizmos stay visible/clickable there; the
// AI render pipeline's export camera is left at the default mask (layer 0
// only) so it never sees them, without needing to hide-and-restore anything
// — critical for AxesHelper since hiding it via `.visible = false` would
// also hide any real content parented under it (Three.js's render traversal
// skips a whole subtree once it hits an invisible object).
export const HELPER_LAYER = 1;

const textureLoader = new THREE.TextureLoader();
const gltfLoader = new GLTFLoader();

// Keyed by URL — a GLB is imported as a *tree* of Scene3D nodes (see
// buildNodesFromGLTFScene), one per mesh in the file, all pointing at the
// same uploaded URL but a different `modelNodePath` within it (see
// resolveModelNodeAtPath) — so every one of those nodes, across every full
// graph rebuild (which happens on every edit — see buildSceneGraph's own
// comment), shares one fetch and one parse instead of re-downloading and
// re-parsing the whole file per mesh per rebuild. The cached value is a
// read-only template: never added to a live scene, never mutated, never
// disposed — each consumer (buildObject3D below) clones out just the one
// geometry/material it needs, so per-instance disposal (disposeSceneGraph)
// can never affect this cache or any other node reading from it.
const modelSceneCache = new Map();
function loadModelScene(url) {
  if (!modelSceneCache.has(url)) {
    const promise = fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error(`Failed to fetch model (${res.status})`);
        return res.arrayBuffer();
      })
      .then(
        (buffer) =>
          new Promise((resolve, reject) =>
            gltfLoader.parse(buffer, "", (gltf) => resolve(gltf.scene), reject),
          ),
      )
      .catch((err) => {
        modelSceneCache.delete(url); // don't let one bad response poison every future attempt
        throw err;
      });
    modelSceneCache.set(url, promise);
  }
  return modelSceneCache.get(url);
}

// Lets the importer (Scene3DEditor's handleImportModel) hand over the scene
// it already parsed client-side to check the file's hierarchy — sparing the
// very first render right after upload a redundant fetch+re-parse of bytes
// it already had.
export function primeModelScene(url, scene) {
  modelSceneCache.set(url, Promise.resolve(scene));
}

// `path` is the dot-joined child-index route from `buildNodesFromGLTFScene`
// (e.g. "0.2" = `root.children[0].children[2]`) — index-based rather than
// name-based because glTF node names aren't guaranteed unique, but a given
// file's own hierarchy shape doesn't change between the parse that assigned
// this path and any later parse of the same URL.
function resolveModelNodeAtPath(root, path) {
  if (!path) return null;
  let cur = root;
  for (const part of path.split(".")) {
    cur = cur?.children?.[Number(part)];
  }
  return cur || null;
}

const DIRECTIONAL_TARGET_DISTANCE = 2;

const CAMERA_GIZMO_DISTANCE = 1;

// Default reach of the Depth Map render pass (see the renderMode effect in
// Viewport.jsx) — the distance in front of a camera at which that pass hits
// black, chosen to comfortably cover the default scene (camera at (0,8,8),
// a 10-unit ground plane at the origin). Unrelated to CAMERA_NEAR/FAR above:
// those are clip planes, this is where the depth *value* bottoms out.
export const DEFAULT_Z_DEPTH_DISTANCE = 20;

// Radius of the optional visible-light marker sphere (see buildObject3D's
// light branch) when a node doesn't specify its own `visibleSize`.
export const DEFAULT_LIGHT_VISIBLE_SIZE = 0.25;

const DEFAULT_SHADOW_MAP_SIZE = 2048;
const SHADOW_BIAS = -0.0005;
// Depth bias alone left visible self-shadowing banding across the ground
// (classic shadow acne — worst at a grazing angle, e.g. a plane under a
// shallow directional light); normalBias (offsets along the surface normal
// instead of view depth) is three.js's recommended fix for that case. Kept
// as a *texel* count rather than a fixed world-space offset, since the
// fitted shadow frustum's size (and so its texel's world-space footprint)
// varies with actual scene scale — a fixed offset that was enough for a
// small fitted frustum silently stopped being enough for a larger one.
// SHADOW_NORMAL_BIAS_MAX caps the result: on a large fitted frustum the
// texel-scaled value can grow enough to push the sample point past the
// floor's own geometry entirely ("peter-panning") — losing its shadow
// altogether rather than just softening acne, which is strictly worse.
const SHADOW_NORMAL_BIAS_TEXELS = 1;
const SHADOW_NORMAL_BIAS_MAX = 0.1;
// PCFShadowMap (unlike the now-deprecated-and-aliased PCFSoftShadowMap, see
// Viewport.jsx) genuinely reads `shadow.radius` for multi-tap blur.
const SHADOW_RADIUS = 3;
// A shadow camera's near/far/bounds have to actually contain the scene, or
// content outside its frustum reads back as clamped shadow-map edge texels —
// a hard, wrong-shaped boundary rather than a smooth falloff. No *fixed*
// value can work across arbitrary user-scaled scenes, so these are fit to
// the actual scene content's bounding sphere every rebuild instead (see
// fitShadowCamerasToScene) — this margin just keeps that fit from being
// pixel-perfect-tight (which would clip geometry sitting exactly on the
// boundary after any rounding).
const SHADOW_FIT_MARGIN = 1.2;
const SHADOW_NEAR_MIN = 0.1;

// How far beyond the scene's actual bounding sphere (see
// computeSceneBoundingSphere) the projection backdrop sits — far enough that
// real content always occludes it via ordinary z-testing, close enough to
// keep reasonable depth-buffer precision (nowhere near CAMERA_FAR, which a
// standard perspective projection matrix has almost no usable precision
// left at). A fixed value can't work here for the same reason a fixed
// shadow frustum can't (see SHADOW_FIT_MARGIN) — scenes vary wildly in
// scale, especially once an arbitrary imported GLB is involved.
const BACKDROP_DISTANCE_MARGIN = 5;
const BACKDROP_MIN_DISTANCE = 5;

// A plain LineSegments drawn once in *local* space (a small square facing
// local -Z, plus a line from the origin out to the light's target distance)
// and added as a normal child of the node's own transform group — it just
// inherits position/rotation/scale through the ordinary parent-child chain,
// same as everything else in the scene, no `.lookAt()` or hijacked-matrix
// tricks involved. THREE.DirectionalLightHelper does both of those (it
// re-derives its plane's orientation from the light/target world positions
// every `.update()`, and hijacks its own `.matrix` to the light's matrixWorld
// so it can sit at the scene root) — correct in isolation, but it drifted
// out of sync with the transform-controls gizmo (attached to this same
// group) after any rotation edit, and re-deriving-via-lookAt every frame
// didn't close the gap. A shape that's simply parented like everything else
// can't drift the same way.
const createDirectionalLightGizmo = (color) => {
  const size = 0.5;
  const points = [
    new THREE.Vector3(-size, size, 0),
    new THREE.Vector3(size, size, 0),
    new THREE.Vector3(size, size, 0),
    new THREE.Vector3(size, -size, 0),
    new THREE.Vector3(size, -size, 0),
    new THREE.Vector3(-size, -size, 0),
    new THREE.Vector3(-size, -size, 0),
    new THREE.Vector3(-size, size, 0),
    new THREE.Vector3(0, 0, 0),
    new THREE.Vector3(0, 0, -DIRECTIONAL_TARGET_DISTANCE),
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({
    fog: false,
    toneMapped: false,
  });
  material.color.set(color);
  return new THREE.LineSegments(geometry, material);
};

// Same rationale as createDirectionalLightGizmo above: a plain LineSegments,
// normal child of the group, no hijacked-matrix/root-parenting tricks —
// unlike THREE.CameraHelper. Also deliberately simpler than CameraHelper's
// frustum: just one rectangle (sized from fov/aspect) at a fixed nominal
// distance, not a near-plane-to-far-plane frustum — near/far aren't a
// user-facing concept here, so there's nothing meaningful to draw for them.
const createCameraGizmo = (color, fov, aspect) => {
  const halfHeight = CAMERA_GIZMO_DISTANCE * Math.tan((fov * DEG2RAD) / 2);
  const halfWidth = halfHeight * aspect;
  const z = -CAMERA_GIZMO_DISTANCE;
  const corners = [
    new THREE.Vector3(-halfWidth, halfHeight, z),
    new THREE.Vector3(halfWidth, halfHeight, z),
    new THREE.Vector3(halfWidth, -halfHeight, z),
    new THREE.Vector3(-halfWidth, -halfHeight, z),
  ];
  const origin = new THREE.Vector3(0, 0, 0);
  const points = [
    origin,
    corners[0],
    origin,
    corners[1],
    origin,
    corners[2],
    origin,
    corners[3],
    corners[0],
    corners[1],
    corners[1],
    corners[2],
    corners[2],
    corners[3],
    corners[3],
    corners[0],
  ];
  const geometry = new THREE.BufferGeometry().setFromPoints(points);
  const material = new THREE.LineBasicMaterial({
    fog: false,
    toneMapped: false,
  });
  material.color.set(color);
  return new THREE.LineSegments(geometry, material);
};

// `preset` seeds the per-axis base before `overrides` is layered on top —
// used for geometry-specific defaults (e.g. a plane starting flat) rather
// than the plain origin/no-rotation/unit-scale every other node gets.
const defaultTransform = (overrides = {}, preset = {}) => ({
  position: { x: 0, y: 0, z: 0, ...preset.position, ...overrides.position },
  rotation: { x: 0, y: 0, z: 0, ...preset.rotation, ...overrides.rotation },
  scale: { x: 1, y: 1, z: 1, ...preset.scale, ...overrides.scale },
});

// A plane defaults to lying flat on the ground plane, sized up — an
// upright 1x1 plane isn't a useful starting point the way an upright cube is.
const PLANE_TRANSFORM_PRESET = {
  rotation: { x: 90 },
  scale: { x: 10, y: 10 },
};

// Factory for a new node of a given type, seeded with sensible defaults so it
// drops into the scene already lit/visible rather than at the origin with
// zero scale or a black material.
export function createNode(type, options = {}) {
  const { parent = null, order = 0, transform, ...overrides } = options;
  const base = {
    _id: nanoid(),
    name: type === "mesh" ? "Object" : type[0].toUpperCase() + type.slice(1),
    type,
    parent,
    order,
    visible: true,
    transform: defaultTransform(
      transform,
      type === "mesh" && overrides.geometry === "plane"
        ? PLANE_TRANSFORM_PRESET
        : undefined,
    ),
  };

  if (type === "mesh") {
    return {
      ...base,
      geometry: "cube",
      ...MESH_DEFAULTS,
      ...overrides,
    };
  }
  if (type === "light") {
    return {
      ...base,
      name: "Light",
      lightType: "directional",
      lightColor: "#ffffff",
      intensity: 1,
      castShadow: true,
      visibleInViewport: false,
      visibleSize: DEFAULT_LIGHT_VISIBLE_SIZE,
      ...overrides,
    };
  }
  if (type === "camera") {
    return {
      ...base,
      name: "Camera",
      fov: 50,
      aspect: 16 / 9,
      zDepthDistance: DEFAULT_Z_DEPTH_DISTANCE,
      ...overrides,
    };
  }
  return { ...base, ...overrides }; // null object
}

// A brand-new scene isn't left dark/uncomposed: one point light and one
// camera, framing the origin, matches how every other DCC tool seeds a scene.
export function createDefaultNodes() {
  return [
    createNode("light", {
      order: 0,
      lightType: "point",
      transform: { position: { x: 5, y: 5, z: 5 } },
    }),
    createNode("camera", {
      order: 1,
      transform: {
        position: { x: 0, y: 8, z: 8 },
        rotation: { x: -45, y: 0, z: 0 },
      },
    }),
  ];
}

const RAD2DEG = 180 / Math.PI;

// Reads an already-parsed three.js object's *local* position/rotation/scale
// back into our node transform shape — the inverse of applyTransform below.
// Local (not world) is what we want here: buildNodesFromGLTFScene mirrors
// the file's own parent-child structure 1:1, so each Scene3D node's parent
// is the corresponding Scene3D node for the object's actual three.js parent,
// and local transforms compose exactly the same way through that chain as
// they did in the source file.
const extractLocalTransform = (object3d) => ({
  position: {
    x: object3d.position.x,
    y: object3d.position.y,
    z: object3d.position.z,
  },
  rotation: {
    x: object3d.rotation.x * RAD2DEG,
    y: object3d.rotation.y * RAD2DEG,
    z: object3d.rotation.z * RAD2DEG,
  },
  scale: { x: object3d.scale.x, y: object3d.scale.y, z: object3d.scale.z },
});

// One Scene3D node per three.js object in an imported GLB's hierarchy — a
// mesh becomes a "custom" mesh node (`modelNodePath` is this object's
// route from the file's root, used later to find "the same" object again in
// a freshly parsed copy — see resolveModelNodeAtPath); anything else (an
// empty/organizational node, or a multi-primitive mesh's wrapping group)
// becomes a "null", same as the toolbar's own Null. `path`/`parentId`/
// `order` place each node correctly within the array this recursion builds
// up, all relative to the *new* nodes being created here — the caller
// (buildNodesFromGLTFScene) only has to worry about where the single root
// of that array attaches in the pre-existing scene.
function nodesFromObject3D(object3d, path, parentId, order) {
  const isMesh = !!object3d.isMesh;
  const node = {
    _id: nanoid(),
    name: object3d.name || (isMesh ? "Object" : "Group"),
    type: isMesh ? "mesh" : "null",
    parent: parentId,
    order,
    visible: true,
    transform: extractLocalTransform(object3d),
    ...(isMesh
      ? { geometry: "custom", modelNodePath: path.join("."), ...MESH_DEFAULTS }
      : {}),
  };
  const nodes = [node];
  object3d.children.forEach((child, i) => {
    nodes.push(...nodesFromObject3D(child, [...path, i], node._id, i));
  });
  return nodes;
}

// Parses a just-uploaded (or freshly fetched) GLB's raw bytes, off the main
// scene-graph loader instance — used by Scene3DEditor's handleImportModel to
// inspect a file's hierarchy before turning it into nodes, independent of
// whatever buildSceneGraph/buildObject3D happen to be doing at the time.
export function parseGLTFArrayBuffer(buffer) {
  return new Promise((resolve, reject) =>
    gltfLoader.parse(buffer, "", (gltf) => resolve(gltf.scene), reject),
  );
}

// Converts an imported GLB's parsed scene into a tree of Scene3D nodes: one
// wrapper "null" node named after the file (so the hierarchy panel shows
// what got imported as a single, collapsible unit, the same way a DCC tool's
// import does), with the file's own object hierarchy (see nodesFromObject3D)
// nested underneath it. `modelUrl`/`modelKey` are threaded onto every mesh
// leaf here rather than through the recursion above, since only mesh leaves
// need them and every one of them needs the same pair.
export function buildNodesFromGLTFScene(
  scene,
  { modelUrl, modelKey, name, parent = null, order = 0 },
) {
  const wrapper = {
    _id: nanoid(),
    name: name || "Model",
    type: "null",
    parent,
    order,
    visible: true,
    transform: defaultTransform(),
  };
  const descendants = scene.children.flatMap((child, i) =>
    nodesFromObject3D(child, [i], wrapper._id, i),
  );
  const withModelRef = descendants.map((n) =>
    n.type === "mesh" ? { ...n, modelUrl, modelKey } : n,
  );
  return [wrapper, ...withModelRef];
}

const applyTransform = (object3d, transform) => {
  const { position, rotation, scale } = transform || {};
  if (position) object3d.position.set(position.x, position.y, position.z);
  if (rotation)
    object3d.rotation.set(
      rotation.x * DEG2RAD,
      rotation.y * DEG2RAD,
      rotation.z * DEG2RAD,
    );
  if (scale) object3d.scale.set(scale.x, scale.y, scale.z);
};

// Builds one Object3D (plus helpers) for a single node, without wiring up
// parenting — callers attach it under the right parent afterwards.
//
// Only THREE.PointLightHelper still uses a built-in THREE helper class here.
// It hijacks its own `.matrix` to equal the tracked light's *world* matrix
// directly (with matrixAutoUpdate disabled) — built to sit at the scene root,
// so nesting it inside our per-node transform `group` would double-apply
// that transform. It's returned separately as `sceneHelper` for the caller
// to parent directly under the identity-transformed root instead. Directional
// lights and cameras instead get a small hand-built LineSegments gizmo (see
// createDirectionalLightGizmo/createCameraGizmo) that's just a normal nested
// child of `group` — THREE.DirectionalLightHelper/CameraHelper both used the
// same hijacked-matrix trick, and it visibly desynced from the gizmo after a
// rotation edit (see git history); a plainly-parented shape can't drift the
// same way, so that's the default now unless a built-in helper is simpler
// (PointLightHelper is just a static mesh at the light's position — nothing
// to desync). AxesHelper (for "null") also has no such override and stays a
// normal nested child.
function buildObject3D(
  node,
  {
    shadowMapSize = DEFAULT_SHADOW_MAP_SIZE,
    root,
    onCustomModelLoaded,
    projectionDescriptors,
  } = {},
) {
  let object3d;
  let pickTarget;
  let sceneHelper = null;
  const extraPickTargets = [];

  if (node.type === "mesh" && node.geometry === "custom") {
    // The GLB hasn't loaded yet (loadModelScene below is async) — this group
    // is returned immediately, empty, and populated in place once it
    // resolves. Nothing re-attaches it: it's already parented wherever the
    // rest of buildSceneGraph puts it, so adding the mesh later just makes
    // it appear on the next rendered frame.
    const group = new THREE.Group();
    if (node.modelUrl && node.modelNodePath) {
      loadModelScene(node.modelUrl)
        .then((templateRoot) => {
          // `root` is only undefined if this ran outside buildSceneGraph
          // (e.g. a future direct caller) — treat that as "never torn
          // down" rather than silently dropping the model.
          if (root?.userData.disposed) return;
          const templateNode = resolveModelNodeAtPath(
            templateRoot,
            node.modelNodePath,
          );
          // Not a mesh (or missing) — e.g. the file was re-imported and its
          // internal structure shifted since this node's path was recorded.
          // Nothing to show; leave the group empty rather than guess.
          if (!templateNode?.isMesh) return;
          // Cloned, not shared: this node's own copy of the geometry/
          // material can be freely disposed on the next rebuild
          // (disposeSceneGraph) without affecting the cached template or any
          // other node that also reads from it.
          const material = Array.isArray(templateNode.material)
            ? templateNode.material.map((m) => m.clone())
            : templateNode.material.clone();
          // The projection shader (applyProjectionsToMaterial) needs both
          // sides of a triangle drawn to ever get a chance to test it.
          // Primitive geometry is always DoubleSide (see the branch below) —
          // a GLB exporter that didn't flag a material `doubleSided`
          // (common for CAD-style exports) got half its faces backface-
          // culled before the fragment shader ever ran, on whole submeshes
          // at a time — match primitives here so an imported model behaves
          // the same way under a projection. (Its own facing test is
          // resolved entirely in the fragment shader from screen-space
          // derivatives now — see projection.js — so it doesn't depend on
          // this geometry's vertex normals being present or correct.)
          const geometry = templateNode.geometry.clone();
          (Array.isArray(material) ? material : [material]).forEach((m) => {
            m.side = THREE.DoubleSide;
          });
          if (!node.ignoreProjections && projectionDescriptors?.length) {
            (Array.isArray(material) ? material : [material]).forEach((m) =>
              applyProjectionsToMaterial(m, projectionDescriptors),
            );
          }
          const mesh = new THREE.Mesh(geometry, material);
          mesh.castShadow = true;
          mesh.receiveShadow = true;
          // So a raycast hit on this mesh resolves back to this node —
          // Viewport reads `hits[0].object.userData.nodeId` off whatever was
          // actually hit, not off `group`.
          mesh.userData.nodeId = node._id;
          // Not displaying the projection is only half of "ignore
          // projections" — without this, the node still blocks the
          // projector's occlusion depth pre-pass (projection.js's
          // withProjectionOccludersHidden) and shadows whatever is behind it,
          // even though its own surface shows no trace of the projection.
          // Excluded here, not from the beauty pass — it still renders
          // normally in the live viewport/AI render.
          if (node.ignoreProjections) {
            mesh.userData.excludeFromProjectorOcclusion = true;
          }
          group.add(mesh);
          onCustomModelLoaded?.();
        })
        .catch((err) =>
          console.error("Failed to load custom model", node.modelUrl, err),
        );
    }
    object3d = group;
    pickTarget = group;
  } else if (node.type === "mesh") {
    const makeGeometry =
      GEOMETRY_FACTORIES[node.geometry] || GEOMETRY_FACTORIES.cube;
    // Unlit skips lighting entirely (MeshBasicMaterial) so the mesh renders
    // its flat color/texture regardless of scene lights — roughness/metalness
    // are meaningless there, since PBR shading is what they'd modulate.
    const MaterialClass = node.unlit
      ? THREE.MeshBasicMaterial
      : THREE.MeshStandardMaterial;
    const material = new MaterialClass({ side: THREE.DoubleSide });
    material.color.set(node.color || "#8899aa");
    if (!node.unlit) {
      material.roughness = node.roughness ?? 1;
      material.metalness = node.metalness ?? 0;
    }
    if (node.textureUrl) {
      const texture = textureLoader.load(node.textureUrl);
      texture.colorSpace = THREE.SRGBColorSpace;
      material.map = texture;
      material.transparent = true;
      material.needsUpdate = true;
    }
    if (!node.ignoreProjections && projectionDescriptors?.length)
      applyProjectionsToMaterial(material, projectionDescriptors);
    const mesh = new THREE.Mesh(makeGeometry(), material);
    // Casting is about physically blocking light, independent of how the
    // mesh shades itself, so it casts a shadow even when unlit — but
    // receiving is a lighting effect, so an unlit mesh (which already
    // ignores all lighting for its own shading) skips it too, consistently.
    mesh.castShadow = true;
    mesh.receiveShadow = !node.unlit;
    // See the "custom" branch above's identical comment — not displaying the
    // projection is only half of "ignore projections"; this keeps the node
    // from occluding it for whatever's behind it too.
    if (node.ignoreProjections) {
      mesh.userData.excludeFromProjectorOcclusion = true;
    }
    object3d = mesh;
    pickTarget = mesh;
  } else if (node.type === "light") {
    const color = node.lightColor || "#ffffff";
    const intensity = node.intensity ?? 1;
    const light =
      node.lightType === "point"
        ? new THREE.PointLight(undefined, intensity)
        : new THREE.DirectionalLight(undefined, intensity);
    light.color.set(color);
    if (node.lightType === "point") {
      // Default decay (2, physically-correct inverse-square falloff) needs
      // intensity in the hundreds/thousands to be visible at normal scene
      // distances — this editor only exposes one unitless "Intensity" field
      // shared with DirectionalLight (which has no distance falloff at all),
      // so decay=0 keeps that single number meaningful for either light type
      // instead of silently requiring wildly different scales.
      light.decay = 0;
    }
    if (node.castShadow !== false) {
      light.castShadow = true;
      light.shadow.mapSize.set(shadowMapSize, shadowMapSize);
      light.shadow.bias = SHADOW_BIAS;
      light.shadow.radius = SHADOW_RADIUS;
      // near/far/normalBias (and, for directional, left/right/top/bottom)
      // aren't set here — they depend on the *scene's* bounds, not anything
      // knowable per-node in isolation (normalBias in particular needs to
      // scale with the fitted frustum size, or a texel covering a large
      // world-space area under a fixed bias reintroduces acne). Fit after
      // the full tree is built and world matrices are current — see
      // fitShadowCamerasToScene in buildSceneGraph.
    }
    const group = new THREE.Group();
    if (node.lightType === "point") {
      sceneHelper = new THREE.PointLightHelper(light, 0.2);
      sceneHelper.userData.isEditorHelper = true;
      pickTarget = sceneHelper;
      group.add(light);
    } else {
      // THREE.DirectionalLight ignores its own `.rotation` entirely — its
      // direction is derived purely from `position -> target.position`. Left
      // at the default (an unparented target sitting at the world origin),
      // rotating the node's group would visibly turn the gizmo but never
      // actually change which way the light shines. Parenting the target
      // under the same group, offset along local -Z, makes the real light
      // direction rotate together with the gizmo.
      light.target.position.set(0, 0, -DIRECTIONAL_TARGET_DISTANCE);
      const gizmo = createDirectionalLightGizmo(color);
      gizmo.userData.isEditorHelper = true;
      pickTarget = gizmo;
      group.add(light, light.target, gizmo);
    }
    // Optional stand-in for the light fixture itself — unlike the gizmos
    // above (tagged isEditorHelper, hidden from every real render), this is
    // a normal visible mesh: the point of "Visible in Viewport" is to give
    // the AI render pipeline an actual glowing shape in the beauty pass to
    // anchor a description/position to (see buildSemanticLights in
    // renderPipeline.js, which only includes a light in scene.json's
    // "lights" once this is on), not just an editor-only debug aid.
    const visibleSize = node.visibleSize ?? DEFAULT_LIGHT_VISIBLE_SIZE;
    const visibleSphere = new THREE.Mesh(
      new THREE.SphereGeometry(visibleSize, 32, 16),
      new THREE.MeshBasicMaterial({ color, toneMapped: false }),
    );
    visibleSphere.visible = !!node.visibleInViewport;
    visibleSphere.userData.nodeId = node._id;
    // Excluded from the projector's own occlusion depth pre-pass (see
    // projection.js's withProjectionOccludersHidden) but NOT from anything
    // else — this small marker still renders normally in the live viewport
    // and the AI beauty pass, it just shouldn't behave as a solid occluder
    // there: a light sitting anywhere inside a projector's frustum would
    // otherwise punch an unexplained hole in the projected image exactly at
    // its position.
    visibleSphere.userData.excludeFromProjectorOcclusion = true;
    group.add(visibleSphere);
    // Otherwise the sphere just sits on top of (and dwarfs) the actual
    // pickable target — a point light's PointLightHelper octahedron or a
    // directional light's tiny gizmo lines — so a click that visibly lands
    // on the sphere's surface almost never also lands on that thin
    // underlying geometry, and the light reads as unclickable. Only added
    // while actually visible, matching the sphere's own `.visible` above —
    // a hidden node has nothing there to click.
    if (node.visibleInViewport) extraPickTargets.push(visibleSphere);
    // Looked up by fitShadowCamerasToScene (buildSceneGraph) once the full
    // tree's world matrices are current — same pattern as the camera node's
    // `userData.camera` below.
    group.userData.light = light;
    // Looked up by computeLightBounds (renderPipeline.js) to compute a real
    // screen-space bounding box for this light the same way it does for any
    // other mesh, once it's actually visible.
    group.userData.visibleSphere = visibleSphere;
    object3d = group;
  } else if (node.type === "camera") {
    const fov = node.fov || 50;
    const aspect = node.aspect || 16 / 9;
    // This aspect only matters for the gizmo's own shape and for the
    // frustum-independent near/far below — the viewport temporarily
    // overrides the camera's *own* `.aspect` to the real container aspect
    // while piloting, to avoid a stretched render (see Viewport.jsx).
    const camera = new THREE.PerspectiveCamera(
      fov,
      aspect,
      CAMERA_NEAR,
      CAMERA_FAR,
    );
    camera.updateProjectionMatrix();
    // Enabled so this camera, when it's the one *piloting* the live
    // viewport, still sees other nodes' HELPER_LAYER-only gizmos (e.g. a
    // null's AxesHelper) — see HELPER_LAYER's own comment. The AI render
    // pipeline's export camera is a separate, freshly-built camera left at
    // the default mask, so this has no effect on exported images.
    camera.layers.enable(HELPER_LAYER);
    const gizmo = createCameraGizmo("#7c9cff", fov, aspect);
    gizmo.userData.isEditorHelper = true;
    const group = new THREE.Group();
    group.add(camera, gizmo);
    object3d = group;
    pickTarget = gizmo;
    // Looked up by the viewport's camera-view switcher: the camera itself
    // sits at identity offset inside `group`, so group's transform (which the
    // rest of the editor treats as "the node's transform") drives its world
    // position/orientation automatically.
    group.userData.camera = camera;
    // Looked up by the same switcher to hide this camera's own frustum gizmo
    // while it's the one piloting the viewport — its lines sit right around
    // the camera's own origin, so rendering *through* this camera puts the
    // near edges of its own frustum directly in view as a bogus border.
    group.userData.gizmo = gizmo;
  } else {
    // No wrapping group needed — AxesHelper is itself a normal Object3D
    // (LineSegments), so it can directly carry the node's transform and
    // parent other nodes' children under it like any group. Excluded from
    // exports via HELPER_LAYER rather than `.visible` — see that constant's
    // comment for why (this is exactly the "can parent real content" case).
    object3d = new THREE.AxesHelper(0.5);
    object3d.layers.set(HELPER_LAYER);
    pickTarget = object3d;
  }

  object3d.userData.nodeId = node._id;
  object3d.userData.nodeType = node.type;
  pickTarget.userData.nodeId = node._id;
  applyTransform(object3d, node.transform);
  object3d.visible = node.visible !== false;
  if (sceneHelper) sceneHelper.visible = object3d.visible;

  return { object3d, pickTarget, sceneHelper, extraPickTargets };
}

// Rebuilds the whole Three.js hierarchy from the flat `nodes` array. Scenes
// here are small (editor content, not runtime assets), so a full rebuild per
// data change is simpler and far less bug-prone than incrementally
// reconciling a diff — the cost is negligible at this scale. Deliberately
// takes only `nodes`: selection is highly transient (changes on every click,
// gizmo-mode keypress, etc.) and doesn't belong in the same rebuild as actual
// scene edits — see the selection-outline effect in Viewport.jsx, which
// updates a lightweight BoxHelper directly instead of triggering this.
export function buildSceneGraph(
  nodes,
  {
    ambientLight,
    shadowMapSize,
    projectionDescriptors,
    projectionPoses,
    onCustomModelLoaded,
  } = {},
) {
  const root = new THREE.Group();
  // Flips to true in disposeSceneGraph — checked by a "custom" mesh's async
  // GLB load (see buildObject3D) before it mutates anything, so a model that
  // finishes loading after this graph has already been torn down (another
  // edit triggered a rebuild, or the editor closed) doesn't attach content to
  // — or steal a shadow refit for — a graph nothing is rendering anymore.
  root.userData.disposed = false;
  const objectsById = new Map();
  const pickables = [];

  // Scene-wide fill light from the "World" settings — not a node, so it
  // isn't part of the per-node build loop below.
  if (ambientLight) {
    root.add(
      new THREE.AmbientLight(
        ambientLight.color || "#ffffff",
        ambientLight.intensity ?? 0,
      ),
    );
  }

  const byId = new Map(nodes.map((n) => [n._id, n]));
  const built = new Map();
  const sceneHelpers = [];

  // Keeps every visible projection's own backdrop (projection.js's
  // createProjectionBackdrop) sized/positioned beyond whatever real content
  // actually exists right now — called alongside fitShadowCamerasToScene,
  // both synchronously below and again once an async-loading GLB's real
  // bounds are known (see refitShadowsForLoadedModel), for the same reason:
  // an empty/small scene at the first call shouldn't leave a later-loaded,
  // much larger model poking through a backdrop that was sized for the
  // smaller scene. One backdrop plane per visible projection (keyed by that
  // projection's own _id) — collapsing to one shared plane isn't viable, a
  // plane sized/oriented for one projector's own axis can be near-degenerate
  // at a grazing angle to a *different* projector's axis. A local closure Map
  // (not module-level, unlike projection.js's depthTargets): this whole
  // function rebuilds the graph from scratch every call, so it's naturally
  // recreated fresh each time and needs no separate pruning — disposeSceneGraph's
  // generic traversal already disposes every backdrop mesh the same as
  // anything else in the graph.
  const projectionBackdrops = new Map();
  const fitBackdropToScene = () => {
    if (!projectionDescriptors?.length || !projectionPoses?.length) return;
    const sphere = computeSceneBoundingSphere(nodes, built);
    for (const pose of projectionPoses) {
      const posePosition = pose.cameraTransform?.position;
      const cameraPosition = new THREE.Vector3(
        posePosition?.x ?? 0,
        posePosition?.y ?? 0,
        posePosition?.z ?? 0,
      );
      const distance = Math.max(
        cameraPosition.distanceTo(sphere.center) +
          sphere.radius +
          BACKDROP_DISTANCE_MARGIN,
        BACKDROP_MIN_DISTANCE,
      );
      const existing = projectionBackdrops.get(pose._id);
      if (!existing) {
        // Every backdrop gets the FULL visible descriptor stack (not just
        // the one layer it's physically oriented for) — see
        // createProjectionBackdrop's own comment on why overlap between two
        // projectors' backdrop planes still needs to composite correctly.
        const backdrop = createProjectionBackdrop(
          pose,
          projectionDescriptors,
          distance,
        );
        if (backdrop) {
          // Visible in the live viewport (every camera that renders it
          // enables this layer), invisible to the beauty-render export
          // camera and to the projector's own occlusion depth pre-pass —
          // both stay at the default layer mask. Same mechanism as a "null"
          // node's AxesHelper — see HELPER_LAYER's own comment above.
          backdrop.layers.set(HELPER_LAYER);
          projectionBackdrops.set(pose._id, backdrop);
          root.add(backdrop);
        }
      } else {
        repositionProjectionBackdrop(existing, pose, distance);
      }
    }
  };

  // A custom model's shadow-relevant (and backdrop-relevant) bounds don't
  // exist yet at the synchronous fits below (the GLB is still in flight) —
  // this re-fits once it lands, using the same `nodes`/`built` this closure
  // already has, so both catch up to the model's real bounds instead of
  // staying stuck at whatever an empty/other-content scene produced.
  const refitShadowsForLoadedModel = () => {
    root.updateMatrixWorld(true);
    fitShadowCamerasToScene(
      nodes,
      built,
      shadowMapSize ?? DEFAULT_SHADOW_MAP_SIZE,
    );
    fitBackdropToScene();
    // The caller's own hook — e.g. Viewport.jsx re-rendering the projector's
    // occlusion depth map, which was last captured before this GLB's mesh
    // ever existed to cast anything into it.
    onCustomModelLoaded?.();
  };

  for (const node of nodes) {
    const { object3d, pickTarget, sceneHelper, extraPickTargets } =
      buildObject3D(node, {
        shadowMapSize,
        root,
        onCustomModelLoaded: refitShadowsForLoadedModel,
        projectionDescriptors,
      });
    built.set(node._id, object3d);
    objectsById.set(node._id, object3d);
    pickables.push(pickTarget, ...extraPickTargets);
    if (sceneHelper) sceneHelpers.push(sceneHelper);
  }

  // Attach in an order that guarantees a parent is already attached to the
  // root (or another attached node) before its children are — nodes may not
  // be listed in parent-before-child order in the array.
  const attached = new Set();
  const attach = (id) => {
    if (attached.has(id)) return;
    const node = byId.get(id);
    const object3d = built.get(id);
    if (!node || !object3d) return;
    if (node.parent && byId.has(node.parent)) {
      attach(node.parent);
      built.get(node.parent).add(object3d);
    } else {
      root.add(object3d);
    }
    attached.add(id);
  };
  for (const node of nodes) attach(node._id);

  root.updateMatrixWorld(true);
  fitShadowCamerasToScene(
    nodes,
    built,
    shadowMapSize ?? DEFAULT_SHADOW_MAP_SIZE,
  );
  fitBackdropToScene();

  // Added directly under `root` (identity transform) rather than nested under
  // their node's own group — see the note on buildObject3D. Their `.update()`
  // reads the light/camera's *world* matrix, which is only correct now that
  // the full hierarchy above has had its matrices computed.
  for (const helper of sceneHelpers) {
    helper.update();
    root.add(helper);
  }

  return { root, objectsById, pickables, sceneHelpers };
}

// Shared by fitShadowCamerasToScene and the projection backdrop (below) —
// both need "how big is the actual scene content right now, and where's its
// center," from the exact same built Object3D instances, so a hidden node
// or one whose geometry hasn't attached yet is excluded identically for
// both. Callers apply their own margin on top of the raw radius returned
// here — a shadow frustum and a backdrop's stand-off distance want
// different amounts of headroom.
function computeSceneBoundingSphere(nodes, built) {
  const sceneBox = new THREE.Box3();
  for (const node of nodes) {
    if (node.type !== "mesh" || node.visible === false) continue;
    const obj = built.get(node._id);
    if (obj) sceneBox.expandByObject(obj);
  }
  const sphere = sceneBox.getBoundingSphere(new THREE.Sphere());
  if (
    !sceneBox.isEmpty() &&
    Number.isFinite(sphere.radius) &&
    sphere.radius > 0
  ) {
    return sphere;
  }
  // No mesh content yet (empty scene, or only lights/cameras present so
  // far) — a small sane default so a freshly added light/backdrop still
  // gets a valid (non-zero, non-NaN) frustum/distance.
  return new THREE.Sphere(new THREE.Vector3(0, 0, 0), 10);
}

// Fits each shadow-casting light's shadow camera to the bounding sphere of
// the scene's actual mesh content, recomputed every rebuild — called only
// after `root.updateMatrixWorld(true)`, since it needs every mesh's and
// light's final *world* position/bounds, not local ones. A fixed frustum
// can't work across arbitrary user-scaled scenes: content sitting outside it
// reads back as clamped shadow-map edge texels — a hard, wrong-shaped
// boundary — rather than gracefully not being shadowed; picking one large
// enough constant is a guessing game that this sidesteps entirely.
function fitShadowCamerasToScene(nodes, built, shadowMapSize) {
  const sphere = computeSceneBoundingSphere(nodes, built).clone();
  sphere.radius *= SHADOW_FIT_MARGIN;

  // How much world space one shadow-map texel covers, given the frustum size
  // just fitted above — the basis for a normalBias that actually scales with
  // it (see SHADOW_NORMAL_BIAS_TEXELS/_MAX's own comments).
  const normalBias = Math.min(
    ((sphere.radius * 2) / shadowMapSize) * SHADOW_NORMAL_BIAS_TEXELS,
    SHADOW_NORMAL_BIAS_MAX,
  );

  const lightPos = new THREE.Vector3();
  const targetPos = new THREE.Vector3();
  const worldDir = new THREE.Vector3();
  for (const node of nodes) {
    if (node.type !== "light" || node.castShadow === false) continue;
    const group = built.get(node._id);
    const light = group?.userData.light;
    if (!light?.castShadow) continue;

    if (node.lightType === "point") {
      // Point-light shadows are an omnidirectional cube map centered exactly
      // on the light's real position — no "aim" to correct, only distance.
      light.getWorldPosition(lightPos);
      const distanceToCenter = lightPos.distanceTo(sphere.center);
      light.shadow.camera.near = Math.max(
        distanceToCenter - sphere.radius,
        SHADOW_NEAR_MIN,
      );
      light.shadow.camera.far = distanceToCenter + sphere.radius;
    } else {
      // A directional light's actual illumination is purely direction-based
      // — position has zero visual effect — but three.js's shadow camera
      // always tracks light.position/target.position exactly. Left at
      // wherever the user dragged the light's icon, the shadow camera's
      // frustum stayed centered on THAT point instead of the scene, so any
      // icon placement not already aligned with the scene center clipped
      // part of it out of frame. Fixed by repositioning the (invisible)
      // light + target — decoupled from the visible gizmo icon, a separate
      // child of the same group, which stays exactly where the user put it —
      // to stand off from the scene's bounding-sphere center along the
      // light's existing direction, always framing the whole scene exactly.
      light.getWorldPosition(lightPos);
      light.target.getWorldPosition(targetPos);
      worldDir.subVectors(targetPos, lightPos).normalize();
      if (worldDir.lengthSq() === 0) worldDir.set(0, -1, 0); // degenerate fallback: straight down

      const standoff = sphere.radius * SHADOW_FIT_MARGIN;
      targetPos.copy(sphere.center);
      lightPos.copy(sphere.center).addScaledVector(worldDir, -standoff);

      light.position.copy(group.worldToLocal(lightPos));
      light.target.position.copy(group.worldToLocal(targetPos));
      light.updateMatrixWorld(true);
      light.target.updateMatrixWorld(true);

      light.shadow.camera.near = Math.max(
        standoff - sphere.radius,
        SHADOW_NEAR_MIN,
      );
      light.shadow.camera.far = standoff + sphere.radius;
      light.shadow.camera.left = -sphere.radius;
      light.shadow.camera.right = sphere.radius;
      light.shadow.camera.top = sphere.radius;
      light.shadow.camera.bottom = -sphere.radius;
    }
    light.shadow.normalBias = normalBias;
    light.shadow.camera.updateProjectionMatrix();
  }
}

// Frees GPU resources for a previously-built graph before it's discarded —
// rebuilding on every edit would otherwise leak a geometry/material pair per
// node per edit for the lifetime of the editing session.
export function disposeSceneGraph(root) {
  // Marks any in-flight "custom" model load for this graph as moot — see the
  // matching check in buildObject3D/buildSceneGraph.
  root.userData.disposed = true;
  root.traverse((obj) => {
    if (obj.geometry) obj.geometry.dispose();
    if (obj.material) {
      const materials = Array.isArray(obj.material)
        ? obj.material
        : [obj.material];
      materials.forEach((m) => {
        // `m.map` was the only texture slot worth checking back when every
        // material here was one this file built itself (see buildObject3D —
        // at most a base color map). A "custom" node's materials come from
        // an arbitrary imported GLB and can carry any number of PBR texture
        // slots (normalMap, roughnessMap, aoMap, emissiveMap, ...) — walking
        // every own property for a Texture is what actually catches all of
        // them instead of just the one this code used to know about.
        for (const key in m) {
          if (m[key]?.isTexture) m[key].dispose();
        }
        m.dispose();
      });
    }
    if (obj.dispose) obj.dispose(); // helpers (light/camera helpers, AxesHelper)
  });
}
